import React, {useMemo} from 'react';
import classNames from 'classnames';
import {IntlShape} from 'react-intl';
import {SVGS} from '../../../../../assets/images';
import StateIcon from '../../../../../components/Schedule/timesheet-state-icon';
import TimeSheetActionMenu from '../../../../../components/Schedule/time-shift/components/action-menu';
import {Lx} from '../../../../../i18n/consts';
import {PopupMenuAction} from '../../../../../components/shared/popup-menu/types';
import {
    DateRangeFilter,
    TimeSheetGroupType,
    TimeSheetItem,
    TimeSheetItemStatus,
    TimeSheetItemType,
    UpdateStatusAbility
} from '../../../../../features/schedule/types';
import {TiminatorAbsenceTypes, TiminatorTypes} from '../../../../../features/types/types';
import {formatHours, getTimeBankLabel} from './cells';
import TimesheetProjectMobileItem, {TimesheetProjectMobileItemAction, TimesheetProjectMobileItemRow} from './timesheet-project-mobile-item';
import {TimesheetRowAction} from './timesheet-row-preview';
import TimesheetActionsMenu from './actions-menu';
import {usePlannedHours} from '../hooks/planned-hours';
import {useMobileGroupOverlay} from './hooks/use-mobile-group-overlay';
import styles from './timesheet-mobile-view.module.scss';
import {
    getTimesheetGridTotals,
    getHoursToApprove,
    getTimesheetItemSummaryHours,
    TimeSheetGridTotals
} from '../../../../../helpers/timesheet-summary-hours';

type GroupedItem = {
    id: string;
    title: string;
    items: TimeSheetItem[];
    rows: TimesheetProjectMobileItemRow[];
    totals: TimeSheetGridTotals;
};

type GroupItemProps = {
    group: GroupedItem;
    groupBy: TimeSheetGroupType;
    dateRange?: DateRangeFilter | null;
    renderGroupActions: (items: TimeSheetItem[], totals: TimeSheetGridTotals) => TimesheetProjectMobileItemAction[];
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    rootRef: (node: HTMLDivElement | null) => void;
    headerRef: (node: HTMLDivElement | null) => void;
};

const buildTotalsProps = (
    totals: TimeSheetGridTotals,
    isProjectGroup: boolean,
    plannedHours?: number,
    hidePlanned?: boolean
) => {
    const plannedValue = plannedHours ?? 0;
    const hideTracking = isProjectGroup;
    const shouldHidePlanned = hideTracking || hidePlanned;

    return {
        workHours: totals.workHours,
        absenceHours: totals.absenceHours,
        approved: totals.approved,
        approvableHours: totals.approvable,
        billed: totals.billed,
        billable: totals.billable,
        totalHours: totals.total,
        plannedHours: isProjectGroup ? undefined : plannedValue,
        hideTrackingHours: hideTracking,
        hidePlanned: shouldHidePlanned
    };
};

const GroupItem: React.FC<GroupItemProps> = ({
    group,
    groupBy,
    dateRange,
    renderGroupActions,
    expanded,
    onExpandedChange,
    rootRef,
    headerRef
}) => {
    const isProjectGroup = groupBy === TimeSheetGroupType.PROJECTVIEW;
    const showPlannedUnderTotal = groupBy === TimeSheetGroupType.EMPLOYEEVIEW;
    const plannedHoursParams = React.useMemo(() => {
        if (isProjectGroup) {
            return null;
        }
        const employeeId = group.items[0]?.employeeId;

        if (!employeeId || !dateRange?.startDate || !dateRange?.endDate) {
            return null;
        }

        return {
            employeeId,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        };
    }, [dateRange?.endDate, dateRange?.startDate, group.items, isProjectGroup]);
    const { plannedHours } = usePlannedHours(plannedHoursParams);
    const actions = renderGroupActions(group.items, group.totals);
    const totalsProps = buildTotalsProps(
        group.totals,
        isProjectGroup,
        plannedHoursParams ? plannedHours : 0,
        showPlannedUnderTotal
    );

    return (
        <TimesheetProjectMobileItem
            projectName={group.title}
            totals={totalsProps}
            rows={group.rows}
            actions={actions}
            expanded={expanded}
            onExpandedChange={onExpandedChange}
            showPlannedUnderTotal={showPlannedUnderTotal}
            groupKey={group.id}
            rootRef={rootRef}
            headerRef={headerRef}
        />
    );
};

type Props = {
    data: TimeSheetItem[];
    groupBy: TimeSheetGroupType;
    shiftTypes: TiminatorTypes;
    absenceTypes: TiminatorAbsenceTypes;
    intl: IntlShape;
    permissions: { approve: UpdateStatusAbility; billable: UpdateStatusAbility };
    canManageEntry: (item: TimeSheetItem) => boolean;
    onApproveAllClick: (items: TimeSheetItem[]) => Promise<void>;
    onBillAllClick: (items: TimeSheetItem[]) => Promise<void>;
    onDisapproveAllClick: (items: TimeSheetItem[]) => Promise<void>;
    onCancelBillAllClick: (items: TimeSheetItem[]) => Promise<void>;
    onInlineApproveClick: (item: TimeSheetItem, approve: boolean) => Promise<void>;
    onInlineBillClick: (item: TimeSheetItem, billable: boolean) => Promise<void>;
    onEditItemClick: (item: TimeSheetItem) => Promise<void>;
    onCopyItemClick: (item: TimeSheetItem) => Promise<void>;
    onDeleteItemClick: (item: TimeSheetItem) => void;
    onAddNewShiftClick: (item: TimeSheetItem) => Promise<void>;
    dateRange?: DateRangeFilter | null;
    className?: string;
    disabled?: boolean;
};

const TimesheetMobileView: React.FC<Props> = ({
    data,
    groupBy,
    shiftTypes,
    absenceTypes,
    intl,
    permissions,
    canManageEntry,
    onApproveAllClick,
    onBillAllClick,
    onDisapproveAllClick,
    onCancelBillAllClick,
    onInlineApproveClick,
    onInlineBillClick,
    onEditItemClick,
    onCopyItemClick,
    onDeleteItemClick,
    onAddNewShiftClick,
    dateRange,
    className,
    disabled = false
}) => {
    const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>({});

    const getTypeName = React.useCallback((item: TimeSheetItem): string => {
        if (item.timeshiftType === TimeSheetItemType.SHIFT) {
            return shiftTypes.items.find((type) => type.id === item.timeshiftTypeId)?.name ?? '';
        }

        return absenceTypes.items.find((type) => type.id === item.timeshiftTypeId)?.name ?? '';
    }, [absenceTypes.items, shiftTypes.items]);

    const buildApproveAction = React.useCallback((item: TimeSheetItem): TimesheetRowAction => {
        const shouldApprove = item.status === TimeSheetItemStatus.NORMAL;
        const approveAbility = permissions.approve;

        const actionDisabled = disabled
            || (approveAbility
                ? shouldApprove
                    ? !approveAbility.canApply
                    : !approveAbility.canCancel
                : false);
        const label = shouldApprove
            ? intl.formatMessage({ id: Lx.Schedule.APPROVE_FORMAT }, { s: formatHours(item.hours ?? 0) })
            : intl.formatMessage({ id: Lx.Schedule.DISAPPROVE_FORMAT }, { s: formatHours(item.hours ?? 0) });

        return {
            id: 'approve',
            icon: (
                <StateIcon
                    status={shouldApprove ? TimeSheetItemStatus.NORMAL : TimeSheetItemStatus.APPROVED}
                    active={!shouldApprove}
                />
            ),
            label,
            onClick: () => {
                if (actionDisabled) {
                    return;
                }

                return onInlineApproveClick(item, shouldApprove);
            },
            disabled: actionDisabled,
            active: !shouldApprove,
            variant: 'approve'
        };
    }, [disabled, intl, onInlineApproveClick, permissions.approve]);

    const buildBillableAction = React.useCallback((item: TimeSheetItem): TimesheetRowAction => {
        const isBillableShift = item.timeshiftType === TimeSheetItemType.SHIFT && item.isBillable;
        const shouldBill = item.status !== TimeSheetItemStatus.BILLABLE;
        const billableAbility = permissions.billable;

        const actionDisabled = disabled || !isBillableShift
            || (billableAbility
                ? shouldBill
                    ? !billableAbility.canApply
                    : !billableAbility.canCancel
                : false);
        const label = shouldBill
            ? intl.formatMessage({ id: Lx.Schedule.BILL_SHIFT_FORMAT }, { s: formatHours(item.hours ?? 0) })
            : intl.formatMessage({ id: Lx.Schedule.CANCEL_BILL_FORMAT }, { s: formatHours(item.hours ?? 0) });

        return {
            id: 'billable',
            icon: <StateIcon status={TimeSheetItemStatus.BILLABLE} active={!shouldBill} />,
            label,
            onClick: () => {
                if (actionDisabled) {
                    return;
                }

                return onInlineBillClick(item, shouldBill);
            },
            disabled: actionDisabled,
            active: !shouldBill,
            variant: 'billable'
        };
    }, [disabled, intl, onInlineBillClick, permissions.billable]);

    const buildActionsMenu = React.useCallback((item: TimeSheetItem): TimesheetRowAction => {
        const shouldApprove = item.status === TimeSheetItemStatus.NORMAL;
        const approveAbility = permissions.approve;
        const isBillableShift = item.timeshiftType === TimeSheetItemType.SHIFT && item.isBillable;
        const shouldBill = item.status !== TimeSheetItemStatus.BILLABLE;
        const billableAbility = permissions.billable;
        const canUseApproveAction = shouldApprove
            ? Boolean(approveAbility?.canApply)
            : Boolean(approveAbility?.canCancel);
        const canUseBillableAction = isBillableShift && (shouldBill
            ? Boolean(billableAbility?.canApply)
            : Boolean(billableAbility?.canCancel));
        const approveDisabled = disabled || !canUseApproveAction;
        const billableDisabled = disabled || !canUseBillableAction;
        const hideApprove = !canUseApproveAction;
        const hideBillable = !canUseBillableAction;

        const handleApproveClick = () => {
            if (approveDisabled) {
                return;
            }
            return onInlineApproveClick(item, shouldApprove);
        };

        const handleBillableClick = () => {
            if (billableDisabled) {
                return;
            }
            return onInlineBillClick(item, shouldBill);
        };

        return {
            id: 'actions',
            label: 'Actions',
            render: (
                <TimeSheetActionMenu
                    intl={intl}
                    status={item.status}
                    timeshiftType={item.timeshiftType}
                    hours={item.hours ?? 0}
                    hideApprovable={hideApprove}
                    hideBillable={hideBillable}
                    disableApprovable={approveDisabled}
                    disableBillable={billableDisabled}
                    onApproveClick={handleApproveClick}
                    onBillableClick={handleBillableClick}
                    onAddNewShiftClick={() => onAddNewShiftClick(item)}
                    onEditTimeClick={() => onEditItemClick(item)}
                    onCopyItemClick={() => onCopyItemClick(item)}
                    onDeleteItemClick={() => onDeleteItemClick(item)}
                    disabled={disabled}
                    hideAddAction={!canManageEntry(item)}
                    hideEditAction={!canManageEntry(item)}
                    hideCopyAction={!canManageEntry(item)}
                    hideDeleteAction={!canManageEntry(item)}
                />
            )
        };
    }, [
        disabled,
        intl,
        onAddNewShiftClick,
        onDeleteItemClick,
        onEditItemClick,
        onCopyItemClick,
        onInlineApproveClick,
        onInlineBillClick,
        canManageEntry,
        permissions.approve,
        permissions.billable
    ]);

    const buildRowActions = React.useCallback(
        (item: TimeSheetItem): TimesheetRowAction[] => ([
            buildActionsMenu(item),
            buildApproveAction(item),
            buildBillableAction(item)
        ]),
        [buildActionsMenu, buildApproveAction, buildBillableAction]
    );

    const groups = useMemo<GroupedItem[]>(() => {
        const map = new Map<string, TimeSheetItem[]>();

        data.forEach((item) => {
            const groupKey = groupBy === TimeSheetGroupType.EMPLOYEEVIEW
                ? `employee-${item.employeeId}`
                : `project-${item.targetId}`;
            const existing = map.get(groupKey) ?? [];
            existing.push(item);
            map.set(groupKey, existing);
        });

        return Array.from(map.entries()).map(([groupKey, items]) => {
            const sample = items[0];
            const title = groupBy === TimeSheetGroupType.EMPLOYEEVIEW ? sample.employeeName : sample.targetName;
            const rows: TimesheetProjectMobileItemRow[] = items.map((item) => ({
                id: item.id,
                date: item.date,
                name: groupBy === TimeSheetGroupType.EMPLOYEEVIEW ? item.targetName : item.employeeName,
                type: getTypeName(item),
                value: formatHours(item.hours ?? 0),
                timeBankLabel: getTimeBankLabel(item.timeBankHours),
                actions: buildRowActions(item)
            }));

            const totals = getTimesheetGridTotals(items);

            return {
                id: groupKey,
                title,
                items,
                rows,
                totals
            };
        });
    }, [buildRowActions, data, getTypeName, groupBy]);

    React.useEffect(() => {
        setExpandedGroups((previousState) => {
            const nextState: Record<string, boolean> = {};
            groups.forEach((group) => {
                nextState[group.id] = previousState[group.id] ?? false;
            });

            const hasSameKeys = Object.keys(previousState).length === Object.keys(nextState).length;
            const hasSameValues = hasSameKeys
                && Object.keys(nextState).every((key) => previousState[key] === nextState[key]);
            return hasSameValues ? previousState : nextState;
        });
    }, [groups]);

    const {
        listRef,
        overlayRef,
        overlayState,
        activeOverlayKeyRef,
        setOverlayHidden,
        scheduleOverlayRecalculation,
        setGroupRootRef,
        setGroupHeaderRef,
    } = useMobileGroupOverlay({
        groups,
        expandedGroups
    });

    const setGroupExpanded = React.useCallback((groupKey: string, isExpanded: boolean) => {
        setExpandedGroups((previousState) => {
            if (previousState[groupKey] === isExpanded) {
                return previousState;
            }
            return {
                ...previousState,
                [groupKey]: isExpanded
            };
        });

        if (!isExpanded && activeOverlayKeyRef.current === groupKey) {
            setOverlayHidden();
        }
        scheduleOverlayRecalculation();
    }, [activeOverlayKeyRef, scheduleOverlayRecalculation, setOverlayHidden]);

    const renderGroupActions = React.useCallback((items: TimeSheetItem[], totals: TimeSheetGridTotals): TimesheetProjectMobileItemAction[] => {
        const approvableHours = getHoursToApprove(items);
        const totalHours = totals.total;
        const approvedHours = totals.approved;
        const billableHours = totals.billable;
        const billedHours = totals.billed;
        const billableItems = items.filter((item) => item.timeshiftType === TimeSheetItemType.SHIFT
            && (item.isBillable || item.status === TimeSheetItemStatus.BILLABLE)
            && getTimesheetItemSummaryHours(item) > 0);
        const approveActive = totals.approvable > 0 && approvedHours >= totals.approvable;
        const billableActive = billableHours > 0
            && billableItems.length > 0
            && billableItems.every((item) => item.status === TimeSheetItemStatus.BILLABLE);
        const approveAbility = permissions.approve;
        const billableAbility = permissions.billable;
        const approveDisabled = disabled
            || items.length === 0
            || totalHours < 1
            || (approveAbility
                ? approveActive
                    ? !approveAbility.canCancel
                    : !approveAbility.canApply
                : false);
        const billableDisabled = disabled
            || items.length === 0
            || billableHours < 1
            || (billableAbility
                ? billableActive
                    ? !billableAbility.canCancel
                    : !billableAbility.canApply
                : false);
        const canApproveActions = approveAbility?.canApply || approveAbility?.canCancel;
        const canBillActions = billableAbility?.canApply || billableAbility?.canCancel;
        const billableHoursToBill = Math.max(billableHours - billedHours, 0);
        const addShiftLabel = intl.formatMessage({ id: Lx.Schedule.CREATE_NEW_TIMESHIFT_SHIFT });

        const approveLabel = approveActive
            ? intl.formatMessage({ id: Lx.Schedule.DISAPPROVE_FORMAT_ALL }, { s: formatHours(approvedHours) })
            : intl.formatMessage({ id: Lx.Schedule.APPROVE_ALL_FORMAT }, { s: formatHours(approvableHours) });

        const billableLabel = billableActive
            ? intl.formatMessage({ id: Lx.Schedule.CANCEL_BILL_FORMAT }, { s: formatHours(billedHours) })
            : intl.formatMessage({ id: Lx.Schedule.BILL_ALL_FORMAT }, { s: formatHours(billableHoursToBill) });

        const template = items[0];
        const menuActions: Array<PopupMenuAction | undefined> = [
            canApproveActions
                ? {
                    name: approveLabel,
                    icon: (
                        <StateIcon
                            status={approveActive ? TimeSheetItemStatus.APPROVED : TimeSheetItemStatus.NORMAL}
                            active={approveActive}
                        />
                    ),
                    callback: () => {
                        if (approveDisabled) {
                            return;
                        }
                        return approveActive
                            ? onDisapproveAllClick(items)
                            : onApproveAllClick(items);
                    },
                    disabled: approveDisabled
                }
                : undefined,
            canBillActions
                ? {
                    name: billableLabel,
                    icon: <StateIcon status={TimeSheetItemStatus.BILLABLE} active={billableActive} />,
                    callback: () => {
                        if (billableDisabled) {
                            return;
                        }
                        return billableActive
                            ? onCancelBillAllClick(items)
                            : onBillAllClick(items);
                    },
                    disabled: billableDisabled
                }
                : undefined,
            template && canManageEntry(template)
                ? {
                    name: addShiftLabel,
                    icon: <SVGS.PlusIcon />,
                    callback: () => {
                        if (!template) {
                            return;
                        }
                        if (!disabled) {
                            onAddNewShiftClick({ ...template, date: '' });
                        }
                    },
                    disabled: disabled || !template
                }
                : undefined
        ];
        const availableMenuActions = menuActions.filter((action): action is PopupMenuAction => !!action);

        const actions: Array<TimesheetProjectMobileItemAction | undefined> = [
            {
                id: 'actions',
                icon: <SVGS.ActionIcon />,
                label: addShiftLabel,
                render: (
                    <TimesheetActionsMenu
                        actions={availableMenuActions}
                        disabled={disabled || availableMenuActions.length === 0}
                    />
                )
            },
            canApproveActions
                ? {
                    id: 'approve',
                    icon: (
                        <StateIcon
                            status={approveActive ? TimeSheetItemStatus.APPROVED : TimeSheetItemStatus.NORMAL}
                            active={approveActive}
                        />
                    ),
                    label: approveLabel,
                    disabled: approveDisabled,
                    active: approveActive,
                    variant: 'approve',
                    onClick: () => {
                        if (approveDisabled) {
                            return;
                        }
                        return approveActive
                            ? onDisapproveAllClick(items)
                            : onApproveAllClick(items);
                    }
                }
                : undefined,
            canBillActions
                ? {
                    id: 'billable',
                    icon: <StateIcon status={TimeSheetItemStatus.BILLABLE} active={billableActive} />,
                    label: billableLabel,
                    disabled: billableDisabled,
                    active: billableActive,
                    variant: 'billable',
                    onClick: () => {
                        if (billableDisabled) {
                            return;
                        }
                        return billableActive
                            ? onCancelBillAllClick(items)
                            : onBillAllClick(items);
                    }
                }
                : undefined
        ];

        return actions.filter((action): action is TimesheetProjectMobileItemAction => Boolean(action));
    }, [
        disabled,
        intl,
        onAddNewShiftClick,
        onApproveAllClick,
        onBillAllClick,
        onCancelBillAllClick,
        onDisapproveAllClick,
        canManageEntry,
        permissions.approve,
        permissions.billable
    ]);

    const activeOverlayGroup = useMemo(
        () => groups.find((group) => group.id === overlayState.activeKey) ?? null,
        [groups, overlayState.activeKey]
    );
    const activeOverlayActions = useMemo(
        () => activeOverlayGroup ? renderGroupActions(activeOverlayGroup.items, activeOverlayGroup.totals) : [],
        [activeOverlayGroup, renderGroupActions]
    );

    return (
        <div ref={listRef} className={classNames(styles.list, className)}>
            {activeOverlayGroup && expandedGroups[activeOverlayGroup.id] && (
                <div
                    ref={overlayRef}
                    className={styles.groupOverlay}
                    style={{
                        top: `${overlayState.top - 1}px`, //-1 to avoid double border
                        left: `${overlayState.left}px`,
                        width: `${overlayState.width}px`,
                        transform: `translateY(${overlayState.offsetY}px)`
                    }}
                >
                    <TimesheetProjectMobileItem
                        projectName={activeOverlayGroup.title}
                        totals={buildTotalsProps(activeOverlayGroup.totals, groupBy === TimeSheetGroupType.PROJECTVIEW, 0, false)}
                        rows={[]}
                        actions={activeOverlayActions}
                        expanded={true}
                        onExpandedChange={(isExpanded) => setGroupExpanded(activeOverlayGroup.id, isExpanded)}
                        renderContent={false}
                        groupKey={activeOverlayGroup.id}
                        className={styles.groupOverlayItem}
                    />
                </div>
            )}
            {groups.map((group) => {
                if (group.id === null||group.id === undefined || group.id == '' || group.title.length == 0) {return null;}
                return <GroupItem
                    key={group.id}
                    group={group}
                    groupBy={groupBy}
                    dateRange={dateRange}
                    renderGroupActions={renderGroupActions}
                    expanded={expandedGroups[group.id] === true}
                    onExpandedChange={(isExpanded) => setGroupExpanded(group.id, isExpanded)}
                    rootRef={setGroupRootRef(group.id)}
                    headerRef={setGroupHeaderRef(group.id)}
                />
            })}
        </div>
    );
};

export default TimesheetMobileView;
